import React, { useEffect, useState, useCallback } from 'react';
import {
  Sparkles, ArrowRight, MessageCircleReply, CheckCircle2,
  Loader2, MessageSquare, Trophy, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Phase 1 / Step 5 — Automation Impact Drill-Down
 *
 * The dashboard tile shows a one-line rollup. Each metric is now clickable —
 * opening a right-side sheet that lists the leads behind that number. Each
 * row deep-links to the relevant Inbox conversation so the operator can
 * respond immediately.
 */

// ── Segment config ──────────────────────────────────────────────────────────

const SEGMENTS = {
  followups: {
    label:     'Auto follow-ups sent',
    icon:      Sparkles,
    iconClass: 'text-violet-600',
    bgClass:   'bg-violet-50',
    emptyText: 'No auto follow-ups have fired yet this week.',
  },
  replies: {
    label:     'Customer replies',
    icon:      MessageCircleReply,
    iconClass: 'text-sky-600',
    bgClass:   'bg-sky-50',
    emptyText: 'No customer replies received this week.',
  },
  approved: {
    label:     'Quotes approved',
    icon:      Trophy,
    iconClass: 'text-emerald-600',
    bgClass:   'bg-emerald-50',
    emptyText: 'No quotes approved this week — yet.',
  },
};

// ── Time formatting ─────────────────────────────────────────────────────────

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

// ── Drill-down sheet ────────────────────────────────────────────────────────

const DrilldownSheet = ({ segment, open, onClose }) => {
  const navigate = useNavigate();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !segment) return undefined;
    let cancelled = false;
    setLoading(true);
    setItems([]);

    (async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken(false);
        if (!token) { setLoading(false); return; }
        const res = await fetch(
          `${API}/api/automation/weekly-impact/drilldown?segment=${segment}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) { setLoading(false); return; }
        const payload = await res.json();
        if (!cancelled && payload?.success) setItems(payload.items || []);
      } catch (err) {
        console.warn('[AutomationImpactTile] drilldown failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [segment, open]);

  const cfg = segment ? SEGMENTS[segment] : null;
  if (!cfg) return null;
  const Icon = cfg.icon;

  const handleRowClick = (item) => {
    onClose?.();
    if (item.conversationId) {
      navigate(`/inbox?conversation=${item.conversationId}`);
    } else if (item.leadId) {
      navigate(`/leads/${item.leadId}`);
    } else {
      navigate('/inbox');
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col"
        data-testid={`drilldown-sheet-${segment}`}
      >
        <SheetHeader className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-full ${cfg.bgClass} flex items-center justify-center shrink-0`}>
              <Icon className={`h-4 w-4 ${cfg.iconClass}`} />
            </div>
            <div className="text-left flex-1 min-w-0">
              <SheetTitle className="text-base font-semibold leading-tight">
                {cfg.label}
              </SheetTitle>
              <SheetDescription className="text-xs text-gray-500">
                This week · tap any row to open the conversation
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto" data-testid={`drilldown-list-${segment}`}>
          {loading ? (
            <div className="flex items-center gap-2 px-5 py-8 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="px-5 py-10 text-center" data-testid={`drilldown-empty-${segment}`}>
              <MessageSquare className="h-7 w-7 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{cfg.emptyText}</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleRowClick(item)}
                    className="w-full flex items-start gap-3 px-5 py-4 hover:bg-gray-50 active:bg-gray-100 text-left transition-colors"
                    data-testid={`drilldown-item-${item.id}`}
                  >
                    <div className={`h-8 w-8 rounded-full ${cfg.bgClass} flex items-center justify-center shrink-0 mt-0.5`}>
                      <Icon className={`h-3.5 w-3.5 ${cfg.iconClass}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {item.leadName || 'Unnamed lead'}
                        </p>
                        <span className="text-[11px] text-gray-400 shrink-0 whitespace-nowrap">
                          {formatRelative(item.when)}
                        </span>
                      </div>
                      {item.detail && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {item.detail}
                        </p>
                      )}
                      {item.leadPhone && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {item.leadPhone}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-300 mt-1 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
            data-testid="drilldown-close"
          >
            <X className="h-3 w-3" /> Close
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ── Tile ────────────────────────────────────────────────────────────────────

const AutomationImpactTile = () => {
  const navigate = useNavigate();
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSeg, setOpenSeg] = useState(null);  // 'followups' | 'replies' | 'approved'

  const refresh = useCallback(async () => {
    try {
      const token = await getAuth().currentUser?.getIdToken(false);
      if (!token) { setLoading(false); return; }
      const res = await fetch(`${API}/api/automation/weekly-impact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setLoading(false); return; }
      const payload = await res.json();
      if (payload?.success) {
        setData({
          followups: Number(payload.followupsSent) || 0,
          replies:   Number(payload.replies)       || 0,
          approved:  Number(payload.quotesApproved) || 0,
        });
      }
    } catch (err) {
      console.warn('[AutomationImpactTile] fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return (
      <div
        className="hidden lg:flex w-full items-center gap-3 bg-violet-50/60 border border-violet-100 rounded-lg px-4 py-3 animate-pulse"
        data-testid="automation-impact-loading"
      >
        <div className="h-9 w-9 rounded-full bg-violet-100" />
        <div className="h-4 w-72 bg-violet-100 rounded" />
      </div>
    );
  }

  if (!data) return null;
  const { followups, replies, approved } = data;
  if (followups === 0 && replies === 0 && approved === 0) return null;

  const Segment = ({ id, value, color, label, plural }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setOpenSeg(id); }}
      className={`inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded hover:bg-white/60 transition-colors ${color} disabled:cursor-default disabled:hover:bg-transparent`}
      disabled={value === 0}
      title={value === 0 ? 'Nothing to drill into' : `View the ${value} ${value === 1 ? label : plural}`}
      data-testid={`automation-impact-${id}-btn`}
    >
      <span className="font-bold" data-testid={`automation-impact-${id}`}>{value}</span>
      <span className="text-gray-700">{value === 1 ? label : plural}</span>
    </button>
  );

  return (
    <>
      <div
        className="hidden lg:flex w-full items-center gap-4 bg-gradient-to-r from-violet-50 to-white border border-violet-100 rounded-lg px-4 py-3 shadow-sm"
        data-testid="automation-impact-tile"
      >
        <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-violet-600" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-700">
            Automation impact · this week
          </p>
          <p className="text-sm mt-0.5 leading-snug">
            <Segment id="followups" value={followups} color="text-violet-700" label="follow-up sent" plural="follow-ups sent" />
            <span className="text-gray-300 mx-1.5">→</span>
            <Segment id="replies"   value={replies}   color="text-sky-700"    label="reply"           plural="replies" />
            <span className="text-gray-300 mx-1.5">→</span>
            <Segment id="approved"  value={approved}  color="text-emerald-700" label="quote approved" plural="quotes approved" />
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/settings/communication')}
          className="hidden md:inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline shrink-0"
          data-testid="automation-impact-tune-btn"
        >
          Tune cadence <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      <DrilldownSheet
        segment={openSeg}
        open={!!openSeg}
        onClose={() => setOpenSeg(null)}
      />
    </>
  );
};

export default AutomationImpactTile;
